import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import type {
  ProfileNameTitle,
  ProfileSalutation,
} from "@/lib/profile/types";
import type { User } from "@supabase/supabase-js";
import { getPhoneValidationError } from "@/lib/auth/messages";

type RegistrationConflicts = {
  email_taken?: boolean;
  registry_taken?: boolean;
};

export type SignUpInput = {
  email: string;
  password: string;
  lastName: string;
  firstName: string;
  salutation: ProfileSalutation;
  nameTitle: ProfileNameTitle | "";
  occupation: "physician";
  professionalTitle: string;
  registryNumber: string;
  phone: string;
  city: string;
  privacyPolicyVersion: string;
  termsVersion: string;
  privacyPolicyOpenedAt: string;
  termsOpenedAt: string;
  privacyPolicyAccepted: boolean;
  termsAccepted: boolean;
  newsletterSubscribed: boolean;
};

export function isAuthSessionMissing(error: unknown) {
  if (!error || typeof error !== "object") {
    return false;
  }

  const name = "name" in error ? String(error.name) : "";
  const message = "message" in error ? String(error.message).toLowerCase() : "";

  return (
    name === "AuthSessionMissingError" ||
    message.includes("auth session missing")
  );
}

export async function getBrowserAuthUser(): Promise<User | null> {
  const supabase = getSupabaseBrowserClient();
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error && !isAuthSessionMissing(error)) {
    throw error;
  }

  return user ?? null;
}

export async function signInWithPassword(email: string, password: string) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    throw error;
  }

  return data;
}

export async function signUpWithProfile(input: SignUpInput) {
  if (
    !input.privacyPolicyOpenedAt ||
    !input.termsOpenedAt ||
    !input.privacyPolicyAccepted ||
    !input.termsAccepted
  ) {
    throw new Error(
      "A regisztrációhoz meg kell nyitnia és el kell fogadnia az adatvédelmi szabályzatot és a felhasználói feltételeket.",
    );
  }

  const phoneError = getPhoneValidationError(input.phone);
  if (phoneError) {
    throw new Error(phoneError);
  }

  const supabase = getSupabaseBrowserClient();
  const { data: conflicts, error: conflictError } = await supabase.rpc(
    "registration_field_conflicts",
    {
      candidate_email: input.email.trim(),
      candidate_registry_number: input.registryNumber.trim(),
    },
  );
  if (conflictError) {
    throw conflictError;
  }

  const availability = (conflicts ?? {}) as RegistrationConflicts;
  const conflictMessages = [
    availability.email_taken
      ? "Ezzel az e-mail címmel már van fiók. Jelentkezzen be."
      : null,
    availability.registry_taken
      ? "Ezzel a pecsétszámmal már van regisztrált fiók."
      : null,
  ].filter((item): item is string => Boolean(item));
  if (conflictMessages.length > 0) {
    throw new Error(conflictMessages.join(" "));
  }

  const { data, error } = await supabase.auth.signUp({
    email: input.email.trim(),
    password: input.password,
    options: {
      data: {
        full_name: `${input.lastName.trim()} ${input.firstName.trim()}`,
        last_name: input.lastName.trim(),
        first_name: input.firstName.trim(),
        salutation: input.salutation,
        name_title: input.nameTitle,
        occupation: input.occupation,
        professional_title: input.professionalTitle,
        registry_number: input.registryNumber.trim(),
        phone: input.phone.trim(),
        city: input.city,
        privacy_policy_version: input.privacyPolicyVersion,
        terms_version: input.termsVersion,
        privacy_policy_opened_at: input.privacyPolicyOpenedAt,
        terms_opened_at: input.termsOpenedAt,
        privacy_policy_accepted: input.privacyPolicyAccepted,
        terms_accepted: input.termsAccepted,
        newsletter_subscribed: input.newsletterSubscribed,
      },
    },
  });

  if (error) {
    throw error;
  }

  if (
    data.user &&
    Array.isArray(data.user.identities) &&
    data.user.identities.length === 0
  ) {
    throw new Error("Ezzel az e-mail címmel már van fiók. Jelentkezzen be.");
  }

  return data;
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
) {
  if (newPassword.length < 8) {
    throw new Error("Az új jelszó legalább 8 karakter legyen.");
  }
  if (currentPassword === newPassword) {
    throw new Error("Az új jelszó nem egyezhet meg a jelenlegi jelszóval.");
  }

  const supabase = getSupabaseBrowserClient();
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError) {
    throw userError;
  }
  if (!user?.email) {
    throw new Error("A jelszó módosításához be kell jelentkeznie.");
  }

  const { error: verificationError } = await supabase.auth.signInWithPassword({
    email: user.email,
    password: currentPassword,
  });
  if (verificationError) {
    throw new Error("A jelenlegi jelszó hibás.");
  }

  const { error: updateError } = await supabase.auth.updateUser({
    password: newPassword,
  });
  if (updateError) {
    throw updateError;
  }
}

export async function requestPasswordReset(email: string) {
  const trimmed = email.trim();
  if (!trimmed) {
    throw new Error("Adja meg az e-mail címét.");
  }

  const supabase = getSupabaseBrowserClient();
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin.replace(/\/$/, "")
      : (process.env.NEXT_PUBLIC_SITE_URL || "https://dev.praxistars.hu").replace(
          /\/$/,
          "",
        );

  const { error } = await supabase.auth.resetPasswordForEmail(trimmed, {
    redirectTo: `${origin}/reset-password`,
  });

  if (error) {
    throw error;
  }
}

export async function updatePasswordAfterRecovery(newPassword: string) {
  if (newPassword.length < 8) {
    throw new Error("Az új jelszó legalább 8 karakter legyen.");
  }

  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  });

  if (error) {
    throw error;
  }
}

export async function signOut() {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.auth.signOut();

  if (error) {
    throw error;
  }
}
